import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  CAPTURE_METHOD_IMAGE_TO_TEXT,
  CAPTURE_METHOD_MANUAL_TEXT,
} from '@bower/shared/constants/capture';
import { ROUTES } from '../../constants/routes';
import { useCreateEquipment, useUploadEquipmentPhoto } from '../../hooks/data/useEquipment';
import { useAppToast } from '../../hooks/ui/useAppToast';
import { destinationProblem } from '../features/create/destination/createDestination';
import type { EquipmentFormState } from './addEquipmentForm';

interface UseAddEquipmentOptions {
  readonly parentCollectionId?: string;
  readonly onDone: () => void;
  readonly onDestinationProblem?: (problem: 'gone' | 'forbidden') => void;
}

export interface AddEquipmentInput {
  readonly form: EquipmentFormState;
  readonly scanConfidence: number | undefined;
  readonly scannedFile: File | null;
}

/**
 * Creates the equipment, uploads the scan photo if there was one, then closes
 * and opens the new record. A create refused because of where it was going is
 * handed to `onDestinationProblem` when the caller can act on it.
 */
export function useAddEquipment({
  parentCollectionId,
  onDone,
  onDestinationProblem,
}: UseAddEquipmentOptions) {
  const navigate = useNavigate();
  const toast = useAppToast();
  const createEquipment = useCreateEquipment();
  const uploadPhoto = useUploadEquipmentPhoto();

  const submit = useCallback(
    async ({ form, scanConfidence, scannedFile }: AddEquipmentInput) => {
      if (!form.title.trim()) {
        toast({ title: 'Title is required', status: 'warning' });
        return;
      }
      let id: string | undefined;
      try {
        const result = await createEquipment.mutateAsync({
          ...(parentCollectionId
            ? { parentEntityType: 'project' as const, parentEntityId: parentCollectionId }
            : {}),
          title: form.title.trim(),
          make: form.make.trim() || undefined,
          model: form.model.trim() || undefined,
          serial: form.serial.trim() || undefined,
          location: form.location.trim() || undefined,
          scanConfidence,
          captureMethod:
            scanConfidence !== undefined
              ? CAPTURE_METHOD_IMAGE_TO_TEXT
              : CAPTURE_METHOD_MANUAL_TEXT,
        });
        id = result.data?.id;
      } catch (error) {
        const problem = destinationProblem(error);
        if (problem && onDestinationProblem) {
          onDestinationProblem(problem);
          return;
        }
      }
      if (!id) {
        toast({ title: 'Failed to add equipment', status: 'error' });
        return;
      }
      if (scannedFile) {
        try {
          await uploadPhoto.mutateAsync({ id, file: scannedFile });
        } catch {
          toast({
            title: 'Photo not saved',
            description: 'Equipment was created but the scan photo could not be uploaded.',
            status: 'warning',
          });
        }
      }
      onDone();
      navigate(ROUTES.equipmentDetail(id));
    },
    [
      parentCollectionId,
      createEquipment,
      uploadPhoto,
      toast,
      onDone,
      onDestinationProblem,
      navigate,
    ],
  );

  return {
    submit,
    isSubmitting: createEquipment.isPending,
    isBusy: createEquipment.isPending || uploadPhoto.isPending,
  };
}
